/*
 * spellWarningEnrich — Reichert FormatRuns mit dem additiven `spellWarning`-
 * Flag an, basierend auf Warnings vom SpellCheckManager.
 *
 * Wird ausschliesslich im Non-Diff-Pfad genutzt (regulaeres Rendering eines
 * Paragraphs). Im Diff-Review-Pfad baut `buildCompositeFormatRuns` die Runs
 * komplett neu, ohne Warnings — das implementiert die Diff-Review-Suppression
 * aus Spec §1/§5.1 von selbst (Manager-Cache bleibt warm, Anzeige unterdrueckt).
 *
 * `spellWarning` ist additiv: Bold/Italic/Code/Style bleibt unveraendert; nur
 * an betroffenen Wort-Bereichen wird das Flag gesetzt, sodass der Renderer
 * eine zusaetzliche Wavy-Underline darueber zeichnen kann.
 */

/**
 * @param {Array<object>} runs - FormatRuns in Display-Koordinaten
 * @param {Array<{start:number, length:number}>} warnings - in Daten-Koordinaten
 * @param {number[]|null} [dataToDisplay] - Mapping wenn Display-Text Marker
 *        ueberspringt (Section-Links). null = identisch.
 * @param {boolean} [dim=false] - true = Wellen abgetoent zeichnen (Absatz
 *        ist nicht der aktive). Wird als `spellWarningDim`-Flag an die
 *        gesplitteten Runs angehaengt.
 * @returns {Array<object>} neue Run-Liste (Splits an Warning-Grenzen)
 */
export function enrichRunsWithSpellWarnings(runs, warnings, dataToDisplay = null, dim = false) {
    if (!runs || runs.length === 0) return runs
    if (!warnings || warnings.length === 0) return runs

    // 1) Warnings in Display-Koordinaten projizieren (oder direkt uebernehmen).
    //    Sortiert nach start, damit der Pro-Run-Scan unten early-break kann
    //    statt bei jedem Run die ganze Warning-Liste linear zu filtern. War
    //    bei Diktat-Sweeps mit > 20 Warnings pro Zeile ein sichtbarer
    //    Hotspot (jeder Run × jede Warning = N×M Filter).
    const dispWarnings = []
    for (const w of warnings) {
        const range = projectRange(w.start, w.length, dataToDisplay)
        if (range && range.end > range.start) dispWarnings.push(range)
    }
    if (dispWarnings.length === 0) return runs
    dispWarnings.sort((a, b) => a.start - b.start)

    // 2) Pro Run an Warning-Grenzen splitten. Da dispWarnings nach start
    //    sortiert ist, koennen wir den Scan abbrechen, sobald wir einen
    //    Warning sehen, dessen start jenseits des Run-Endes liegt.
    const out = []
    for (const run of runs) {
        const overlapping = []
        for (const w of dispWarnings) {
            if (w.start >= run.end) break
            if (w.end > run.start) overlapping.push(w)
        }
        if (overlapping.length === 0) {
            out.push(run)   // keine Warnung in diesem Run -> unveraendert
            continue
        }
        // Sammle alle Schnittpunkte innerhalb des Runs
        const boundaries = new Set([run.start, run.end])
        for (const w of overlapping) {
            if (w.start > run.start && w.start < run.end) boundaries.add(w.start)
            if (w.end   > run.start && w.end   < run.end) boundaries.add(w.end)
        }
        const sorted = Array.from(boundaries).sort((a, b) => a - b)
        for (let i = 0; i < sorted.length - 1; i++) {
            const sliceStart = sorted[i]
            const sliceEnd   = sorted[i + 1]
            const inWarning = overlapping.some(
                w => w.start <= sliceStart && w.end >= sliceEnd
            )
            out.push({
                ...run,
                start: sliceStart,
                end:   sliceEnd,
                spellWarning: inWarning,
                spellWarningDim: inWarning && dim
            })
        }
    }
    return out
}

/**
 * Projiziert einen Daten-Range in den Display-Raum.
 * @returns {{start:number, end:number} | null}
 */
function projectRange(dataStart, length, dataToDisplay) {
    if (!dataToDisplay) {
        return { start: dataStart, end: dataStart + length }
    }
    if (dataStart >= dataToDisplay.length) return null
    const dispStart = dataToDisplay[dataStart]
    const dataEnd   = dataStart + length
    let dispEnd
    if (dataEnd < dataToDisplay.length) {
        dispEnd = dataToDisplay[dataEnd]
    } else {
        // Letzter Daten-Char: Display-Ende = letzte Map-Position + 1
        const lastIdx = dataToDisplay.length - 1
        dispEnd = (dataToDisplay[lastIdx] || 0) + 1
    }
    return { start: dispStart, end: dispEnd }
}
